import { useMemo } from "react";
import { errorNoti } from "../../../../base/BasicNotifications";
import { COMMENT_SCOPE, readError } from "../_config/quarterlyOkrConfig";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";

const selectPending = (state) => state.pending;

// Codes after which our copy of the review is out of date.
const REFETCH_ON = [
  "invalid_state",
  "objective_not_found",
  "comment_not_found",
  "unmarked_objectives",
];

// The review's writes (§9.3), with one error policy: say why, and re-read
// whatever the error says moved on. `review_not_in_progress` re-enters first
// — the review can be reopened by `enter` while the run is still with the
// coach.
const useReviewActions = (qcId) => {
  const pending = useQuarterlyOkrStore(selectPending);

  const actions = useMemo(() => {
    const store = quarterlyOkrStore.getState;
    const refetch = () =>
      store()
        .fetchReview(qcId, { force: true })
        .catch(() => null);

    const fail = async (error) => {
      const { code, message } = readError(error);
      errorNoti(message);
      if (code === "review_not_in_progress") {
        await store().enterReview(qcId);
        refetch();
      } else if (REFETCH_ON.includes(code)) {
        refetch();
      }
      throw error;
    };

    // Comment bodies (§9.3) — exactly one target per scope.
    const targetBody = (target) => {
      if (target.scope === COMMENT_SCOPE.OBJECTIVE) {
        return {
          [`${target.type}_objective_id`]: String(target.objectiveId),
        };
      }
      if (target.scope === COMMENT_SCOPE.MEMBER) {
        return { member_user_id: String(target.userId) };
      }
      return {};
    };

    return {
      refetch,
      mark: (type, objective, status) =>
        store()
          .setMark(qcId, type, objective.objective_id, status)
          .catch(fail)
          .catch(() => null),
      addComment: (target, text) =>
        store()
          .addComment(
            qcId,
            {
              scope_type: target.scope,
              ...targetBody(target),
              comment_text: text,
            },
            commentKey(target),
          )
          .catch(fail),
      deleteComment: (commentId) =>
        store()
          .deleteComment(qcId, commentId)
          .catch(fail)
          .catch(() => null),
    };
  }, [qcId]);

  return { ...actions, pending };
};

// One pending key per comment target, so saving on one row never disables
// another.
export const commentKey = (target) =>
  `comment:${target.scope}:${target.type || ""}:${
    target.objectiveId || target.userId || ""
  }`;

// The comment being deleted right now, if any (for `CommentList`'s spinner).
export const deletingCommentId = (pending) =>
  Object.keys(pending)
    .find((key) => key.startsWith("deleteComment:"))
    ?.slice("deleteComment:".length) || null;

// The comments that belong to one target, out of the review's flat list.
export const commentsFor = (comments, target) =>
  (comments || []).filter((comment) => {
    if (comment.scope_type !== target.scope) return false;
    if (target.scope === COMMENT_SCOPE.OBJECTIVE) {
      return (
        String(comment[`${target.type}_objective_id`]) ===
        String(target.objectiveId)
      );
    }
    if (target.scope === COMMENT_SCOPE.MEMBER) {
      return String(comment.member_user_id) === String(target.userId);
    }
    return true;
  });

export default useReviewActions;
